import React from 'react';
import { cn } from '@/lib/utils';
import EmptyState from './EmptyState';

interface Column<T> {
  key: string;
  label: string;
  align?: 'left' | 'center' | 'right';
  render: (row: T, index: number) => React.ReactNode;
  className?: string;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyExtractor: (row: T) => string;
  emptyText?: string;
  className?: string;
  onRowClick?: (row: T) => void;
}

/**
 * DataTable — Componente oficial do Design System NetUno.
 * Uso obrigatório para tabelas de dados com header + rows.
 * Proibido criar tabelas manuais fora deste componente.
 */
export function DataTable<T>({
  columns,
  data,
  keyExtractor,
  emptyText = 'Nenhum dado encontrado',
  className,
  onRowClick,
}: DataTableProps<T>) {
  if (data.length === 0) {
    return <EmptyState text={emptyText} />;
  }

  return (
    <div className={cn('overflow-x-auto', className)}>
      <table className="data-table">
        <thead>
          <tr className="border-b border-border/40">
            {columns.map(col => (
              <th
                key={col.key}
                className={cn(
                  'text-xs font-semibold text-muted-foreground uppercase tracking-wide py-2 px-3',
                  col.align === 'center' && 'text-center',
                  col.align === 'right' && 'text-right',
                  col.className,
                )}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border/20">
          {data.map((row, idx) => (
            <tr
              key={keyExtractor(row)}
              className={cn(
                'hover:bg-primary/[0.03] transition-colors duration-150',
                onRowClick && 'cursor-pointer',
              )}
              onClick={() => onRowClick?.(row)}
            >
              {columns.map(col => (
                <td
                  key={col.key}
                  className={cn(
                    'py-2.5 px-3',
                    col.align === 'center' && 'text-center',
                    col.align === 'right' && 'text-right',
                    col.className,
                  )}
                >
                  {col.render(row, idx)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export type { Column, DataTableProps };
export default DataTable;
